import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentType, ResolutionSegment } from '@/graphql/generated/schemas'
import { IncidentActionType, SystemAction } from '@/graphql/generated/schemas'
import { MeStateIProps } from '@/redux/me/meSlice'
import { uuid } from '@/utils/helpers'

import {
  CannedIncidentActionType,
  IncidentActionType as IncidentAction,
  IncidentAudioMessageType,
  IncidentEventType,
  IncidentNoteType,
  SegmentWithTextI,
  TimelineOrder,
} from '../types/feedTypes'
import { NonActionItem } from '../types/feedTypes'

export const DEFAULT_CHAR_LIMIT = 250

/**
 * Prevents polling from rebuilding the timeline when only signed URLs change,
 * while still detecting media added to an existing incident note.
 */
export const getTimelineFingerprint = (
  incident: IncidentQuery['incident']
): string | null => {
  if (!incident) return null

  const actionIds = incident.actions?.map((action) => action.id).join(',') ?? ''
  const noteIds =
    incident.notes?.edges
      ?.map((edge) => {
        const mediaIds =
          edge?.node?.media?.map((media) => media?.id).join('+') ?? ''
        return `${edge?.node?.id}:${mediaIds}`
      })
      .join(',') ?? ''
  const audioIds =
    incident.audioMessages?.edges?.map((edge) => edge?.node?.id).join(',') ?? ''
  const eventCount = incident.events?.length ?? 0

  return `${actionIds}|${noteIds}|${audioIds}|${eventCount}`
}

export const getDescriptionMessage = (type: IncidentType) => {
  switch (type) {
    case IncidentType.Tailgating:
      return 'Multiple people passed through a restricted entrance with only one authorized credential.'
    default:
      return ''
  }
}

export const formatEmailRecipientCount = (
  count: number | undefined
): string => {
  const n = count ?? 0
  return `${n} ${n === 1 ? 'person' : 'people'}`
}

export const getActivityTimeline = (
  actions: IncidentAction[],
  events: IncidentEventType,
  audioMessages: IncidentAudioMessageType[],
  notes: IncidentNoteType[],
  me: MeStateIProps,
  shouldShowEmailActions: boolean,
  order: TimelineOrder = TimelineOrder.Desc
) => {
  const hiddenIncidentActions = [
    IncidentActionType.Dispatch,
    IncidentActionType.SendToOntic,
  ]
  const incidentActions = actions.filter(
    (action) => !hiddenIncidentActions.includes(action?.type)
  )

  const updatedIncidentActions = shouldShowEmailActions
    ? addCannedEmailActions(incidentActions, me)
    : incidentActions

  const noteActions = notes.map((note) => ({
    nonActionType: NonActionItem.IncidentNote,
    ...note,
  }))

  const audioMessageActions = audioMessages.map((audioMessage) => ({
    nonActionType: NonActionItem.AudioMessage,
    ...audioMessage,
  }))
  const systemMessages = events
    .filter((item) =>
      Object.prototype.hasOwnProperty.call(item, 'systemMessage')
    )
    .map((systemMessage) => ({
      nonActionType: NonActionItem.SystemAction,
      ...systemMessage,
    })) as unknown as SystemAction[]

  return [
    ...updatedIncidentActions,
    ...audioMessageActions,
    ...noteActions,
    ...systemMessages,
  ].sort((a, b) => {
    const aDate = new Date(a.createdAt).valueOf()
    const bDate = new Date(b.createdAt).valueOf()
    if (aDate !== bDate) {
      return order === TimelineOrder.Desc ? bDate - aDate : aDate - bDate
    }

    const aRank = getPriorityAssignmentRank(a)
    const bRank = getPriorityAssignmentRank(b)
    if (aRank === null || bRank === null) return 0

    return order === TimelineOrder.Desc ? bRank - aRank : aRank - bRank
  })
}

/**
 * Same-transaction rows can tie on createdAt; rank the category default below
 * the rule change so the pair flips with `order`. Null for anything else,
 * leaving other ties to the stable sort (ENG-33962).
 */
const getPriorityAssignmentRank = (item: {
  // Unread — present so the parameter shares a property with every timeline
  // member, which is what stops TS weak-type detection rejecting notes, audio
  // messages, and system actions against an all-optional shape.
  createdAt: string
  type?: string
  isRuleAction?: boolean | null
}): number | null => {
  if (item.type === IncidentActionType.SystemPriorityAssignment) return 0
  if (item.type === IncidentActionType.PriorityChange && item.isRuleAction) {
    return 1
  }
  return null
}

// Should ONLY be used in the Sales enviornment.
// Used to add canned email actions for after every flag action
export const addCannedEmailActions = (
  actions: IncidentAction[],
  me: MeStateIProps
) =>
  actions.flatMap((action) => {
    if (action.type === IncidentActionType.Flag) {
      const cannedEmailAction = {
        id: uuid(),
        createdAt: action?.createdAt,
        type: CannedIncidentActionType.Email,
        __typename: CannedIncidentActionType.Email,
        creator: {
          id: me?.id,
          firstName: me?.firstName,
          lastName: me?.lastName,
          roleName: me?.roleName,
        },
      }
      return [cannedEmailAction, action]
    }
    return action
  })

/**
 * The incident's first device that has an id. `devices` entries are nullable
 * in the schema, so `devices[0]` can be null while a later entry is usable —
 * and a missing id would leave the trace log query without a device filter.
 */
export const getPrimaryDeviceId = (
  incident: Pick<IncidentQuery['incident'], 'devices'> | undefined
): string | undefined =>
  incident?.devices?.find((device) => device?.id)?.id ?? undefined

/**
 * Whether the incident gets the "View Trace Logs" entry point. The drawer is
 * device-scoped, so any incident with a usable device qualifies, whatever its
 * type.
 */
export const shouldShowTraceLogs = (
  incident: Pick<IncidentQuery['incident'], 'devices'> | undefined
): boolean => Boolean(getPrimaryDeviceId(incident))

/**
 * Computes which segments to render, applying truncation if needed.
 *
 * When truncation is not required or content is expanded, returns all segments
 * with their full text. Otherwise, truncates across segments by character count,
 * appending "..." to the last visible segment if it's cut off.
 *
 * @param segments - Array of resolution segments (TEXT or EVENT_REF)
 * @param shouldTruncate - Whether the total content exceeds the character limit
 * @param isExpanded - Whether the user has expanded the content
 * @param characterLimit - Maximum characters to show when truncated
 * @returns Array of segments with their display text
 */
export const getSegmentsToRender = (
  segments: ResolutionSegment[],
  shouldTruncate: boolean,
  isExpanded: boolean,
  characterLimit: number
): SegmentWithTextI[] => {
  if (!shouldTruncate || isExpanded) {
    return segments.map((segment) => ({ segment, text: segment.text }))
  }

  let remainingChars = characterLimit
  const result: SegmentWithTextI[] = []

  for (const segment of segments) {
    if (remainingChars <= 0) break

    if (segment.text.length <= remainingChars) {
      result.push({ segment, text: segment.text })
      remainingChars -= segment.text.length
    } else {
      result.push({
        segment,
        text: segment.text.substring(0, remainingChars) + '...',
      })
      break
    }
  }

  return result
}

export const formatFeedTimestamp = (createdAt: string): string => {
  const date = new Date(createdAt)
  const time = date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const dateStr = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  return `${time} | ${dateStr}`
}
